import { useIntersectionObserver } from '@pancakeswap/hooks'
import { useTranslation } from '@pancakeswap/localization'
import {
	Box,
	Button,
	Card,
	CheckmarkIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	CloseIcon,
	Flex,
	FlexGap,
	IconButton,
	SearchIcon,
	Text,
	useMatchBreakpoints,
	useModal,
	useModalV2,
	useOnClickOutside
} from '@pancakeswap/uikit'
import ConnectWalletButton from 'components/ConnectWalletButton'
import EPageHeader from 'components/EPageHeader'
import Page from 'components/Layout/Page'
import CustomLoader from 'components/Loader/CustomLoader'
import useAccountActiveChain from 'hooks/useAccountActiveChain'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import { CallState, useSingleContractMultipleData } from 'state/multicall/hooks'
import { usePointsFarmingContract } from 'hooks/useContract'
import { Address } from 'viem'
import PoolItem from './components/PoolItem'
import StablePosition from './components/StablePosition'
import V3Position from './components/V3Position'

import AddModalStable from './components/AddModalStable'
import IncreaseModal from './components/IncreaseModal'
import { useAllPools } from './hooks/usePools'
import { useV3Positions } from './hooks/usePositions'

const PageStyled = styled(Page)`
	padding: 20px;
	color: #fff;
	background: transparent;
	max-width: 628px;

  	@media (max-width: 628px) {
		padding: 0;
		max-width: 100%;
	}
`

const PageHeader = styled(EPageHeader)`
	flex-direction: row;

	@media (max-width: 768px) {
		flex-direction: column;
		align-items: flex-start;
	}
`

const InfoBlockWrapper = styled(Flex)`
	@media (max-width: 768px) {
		flex-direction: row;
		justify-content: space-between;
		width: 100%;
		margin-top: 16px;
	}
`

const InfoBlock = styled.div`
	margin-left: 40px;
	white-space: nowrap;

	@media (max-width: 768px) {
		margin-left: 0;
	}
`

const TopControls = styled(Flex)`
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	margin-top: 24px;

	@media (max-width: 768px) {
		flex-direction: column;
		align-items: stretch;
		gap: 16px;
	}
`

const SearchWrapper = styled(Flex)`
	@media (max-width: 768px) {
		order: 2;
	}
`

const MobileOnlyWrapper = styled.div`
	display: none;

	@media (max-width: 768px) {
		display: block;
		width: 100%;
		margin-bottom: 16px;
	}
`

const DesktopOnlyWrapper = styled.div`
	@media (max-width: 768px) {
		display: none;
	}
`

const MobileTabMenu = styled.div`
	width: 100%;
	margin-bottom: 16px;
	position: relative;  // Add this line
`

const MobileTabButton = styled(Button)`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-radius: 16px;
	padding: 12px 16px;
	background: ${({ theme }) => theme.colors.input};
	border: 1px solid ${({ theme }) => theme.colors.cardBorder};
`

const ButtonLabel = styled.span`
	font-size: 14px;
	color: ${({ theme }) => theme.colors.text};
`

const ButtonContent = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`

const DropdownLabel = styled.span`
	font-size: 12px;
	color: ${({ theme }) => theme.colors.textSubtle};
	margin-bottom: 8px;
	display: block;
`

const MobileTabOptions = styled.div`
	position: absolute;
	top: calc(100% + 4px);
	left: 0;
	right: 0;
	background: ${({ theme }) => theme.colors.background};
	border: 1px solid ${({ theme }) => theme.colors.cardBorder};
	border-radius: 16px;
	overflow: hidden;
	z-index: 10;
`

const MobileTabOption = styled(Button)`
	width: 100%;
	text-align: left;
	border-radius: 0;
	padding: 12px 16px;
	background: transparent;
	color: ${({ theme }) => theme.colors.text};
	font-size: 14px;
	font-weight: normal;
	transition: background-color 0.2s;

	&:hover {
		background-color: ${({ theme }) => theme.colors.tertiary};
	}

	&:not(:last-child) {
		border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
	}
`

const TabMenu = styled.div`
  	display: flex;
	position: relative;
	align-items: flex-end;
	// &::before {
	// 	content: '';
	// 	position: absolute;
	// 	top: 9px;
	// 	background: #0000004D;
	// 	border-radius: 24px 24px 0 0;
	// 	bottom: 0;
	// 	left: 0;
	// 	right: 0;
	// 	z-index: -1;
	// 	clip-path: polygon(50% 0, );
	// }
`

const Corner = styled.div<{ color: string, size: number }>`
	width: ${({ size }) => size}px;
	height: ${({ size }) => size}px;
	overflow: hidden;
	position: absolute;
	&::before {
		content: "";
		display: block;
		width: 200%;
		height: 200%;
		position: absolute;
		border-radius: 50%;
	}
	&.top-left {
		left: 0;
		top: 0;
		&::before {
			left: 0;
			top: 0;
			box-shadow: -${({ size }) => size}px -${({ size }) => size}px 0 0 ${({ color }) => color};
		}
	}
	&.top-right {
		right: 0;
		top: 0;
		&::before {
			right: 0;
			top: 0;
			box-shadow: ${({ size }) => size}px -${({ size }) => size}px 0 0 ${({ color }) => color};
		}
	}
	&.bottom-left {
		left: 0;
		bottom: 0;
		&::before {
			left: 0;
			bottom: 0;
			box-shadow: -${({ size }) => size}px ${({ size }) => size}px 0 0 ${({ color }) => color};
		}
	}
	&.bottom-right {
		right: 0;
		bottom: 0;
		&::before {
			right: 0;
			bottom: 0;
			box-shadow: ${({ size }) => size}px ${({ size }) => size}px 0 0 ${({ color }) => color};
		}
	}
`

const TabMenuItem = styled(Text) <{ selected?: boolean }>`
	flex: 1;
	display: flex;
	justify-content: center;
	align-items: center;
	color: ${({ theme }) => theme.colors.textSubtle};
	user-select: none;
	cursor: pointer;
	position: relative;
	${({ selected, theme }) => selected ? `
		background: #FFFFFF08;
		border-radius: 24px 24px 0 0;
		color: ${theme.colors.text};
		// font-weight: bold;
		height: 64px; 
	` : `
		background: #0000004D;
		border-radius: 0 0 24px 24px;
		height: 58px; 
		&:first-child {
			border-radius: 24px 0;
		}
		&:last-child {
			border-radius: 0 24px;
		}
	`}
`

const SearchBox = styled(Flex)`
	flex: 1;
	border: 1px solid #FFFFFF33;
	border-radius: 12px;
	padding: 8px 17px;
	align-items: center;
	svg {
		opacity: 0.3;
	}
	& > input {
		background: transparent;
		border: none;
		outline: 0;
		flex: 1;
		font-size: 14px;
		&::placeholder {
			color: #958BB0;
		}
	}
`

const ButtonWrapper = styled.div`
	position: relative;
`

const CreatePoolButton = styled(Button)`
	padding: 0;
	border-radius: 12px;
	font-weight: bold;
	display: flex;
	align-items: stretch;
  	height: 72px;
	width: 100%;
`

const ImportButton = styled(Button)`
	padding: 0;
	border-radius: 12px;
	font-weight: bold;
	display: flex;
	align-items: center;
  	height: 72px;
	width: 100%;
`

const CreatePoolDropDown = styled(Box)`
    background: #332D3E;
    border-radius: 16px;
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    padding: 16px;
    z-index: 1;
		& > div {
			padding: 16px;
			cursor: pointer;
			&:hover {
				background: #3A3447;
				border-radius: 8px;
			}
		}
`

const DataTable = styled(Card)`
	border: 1px solid #403d3f;
	background: linear-gradient(90deg, #180110 0%, #160110 50%, #140110 100%);
	box-shadow: #fff 0px 2px 20px;
	border-radius: 24px;
	padding: 20px 0;
	margin-top: 20px;

	@media (max-width: 768px) {
		padding: 16px;
	}
`

const TableHeader = styled.div`
	display: grid;
	grid-template-columns: 2.5fr 1fr 1fr 1fr 1.5fr 1.5fr;
	padding: 25px 20px;
	font-weight: bold;
	font-size: 12px;
	color: ${({ theme }) => theme.colors.textSubtle};

	@media (max-width: 768px) {
		display: none;
	}
`

const Field = styled.div`
	display: flex;
	cursor: pointer;
	gap: 4px;
	&.up, &.down {
		color: white;
	}
	&.up:after {
		content: "↑";
	}
	&.down:after {
		content: "↓";
	}
`

// Add this type guard function
function isV2PoolData(data: any): data is V2PoolData {
	return (
		typeof data === 'object' &&
		data !== null &&
		'address' in data &&
		'token0' in data &&
		'token1' in data &&
		'liquidityUSD' in data &&
		'volumeUSD24H' in data &&
		'totalFees24h' in data
	);
}

// Add this type guard function
function isStablePoolData(data: any): data is StablePoolData {
	return (
		typeof data === 'object' &&
		data !== null &&
		'address' in data &&
		'token0' in data &&
		'token1' in data &&
		'liquidityUSD' in data &&
		'volumeUSD24H' in data &&
		'totalFees24h' in data
	);
}

// Add this new styled component near other styled components
const PaginationContainer = styled(Flex)`
	justify-content: center;
	margin-top: 20px;
	align-items: center;
	gap: 8px;
	
	@media (max-width: 768px) {
		justify-content: center;
		flex-wrap: wrap;
		gap: 12px;
	}
`

const PageText = styled(Text)`
	@media (max-width: 370px) {
		width: 100%;
		text-align: center;
		order: -1;  // Makes the page count appear above buttons on very small screens
	}
`

enum ActiveTab {
	ALL, V2, V3, STABLE, POSITIONS, EARN
}

interface V2PoolData {
	address: string;
	token0: { address: string };
	token1: { address: string };
	liquidityUSD: number;
	volumeUSD24H: number;
	totalFees24h: number;
}

interface StablePoolData {
	address: string;
	token0: { address: string };
	token1: { address: string };
	token2?: { address: string };
	liquidityUSD: number;
	volumeUSD24H: number;
	totalFees24h: number;
}

const POOLS_PER_PAGE = 20

const useSum = (...values: number[]) => {
	return values.reduce((sum, item) => {
		return sum + item
	}, 0)
}

const useMemorizedLoading = (newArray: any[], isLoading: boolean, match: (item1: any, item2: any) => boolean) => {
	const [array, setArray] = useState<any[]>([])
	useEffect(() => {
		if (!isLoading && newArray) {
			const _array = [...array]
			newArray.forEach((item) => {
				const index = array.findIndex((a) => match(a, item))
				if (index === -1)
					_array.push(item)
				// else 
				// _array[index] = item
			})
			setArray(_array)
		}
	}, [array, newArray, isLoading, match])
	return array
}

const NoWalletCard = styled(Card)`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 24px;
	text-align: center;
	border: 1px solid #403d3f;
	background: linear-gradient(90deg, #180110 0%, #160110 50%, #140110 100%);
	box-shadow: #fff 0px 2px 20px;
	border-radius: 24px;
	margin-top: 20px;

	@media (max-width: 768px) {
		padding: 16px;
	}
`

const DropDownWrapper = styled(Box)`
	display: flex;
	flex: 1;
	justify-content: space-between;
	align-items: center;
	width: 200px;
	border: 1px solid #FFFFFF33;
	border-radius: 12px;
	position: relative;
	gap: 8px;
	padding: 8px 12px;
`

const DropDownBox = styled(Box)`
	background: #332D3E;
	border-radius: 12px;
	position: absolute;
	margin-top: 2px;
	left: 0;
	right: 0;
	top: 100%;
	padding: 4px;
	z-index: 9999;
	& > hr {
		border-color: #958BB0;
	}
`

const DropDownItem = styled(Box)`
	display: flex;
	justify-content: space-between;
	align-items: center;
    padding: 10px 16px;
    &:hover {
        background: #3A3447;
        border-radius: 8px;
        cursor: pointer;
    }
`

const StyledIconButton = styled(IconButton)`
	background: #FFFFFF0F;
	border-radius: 12px;
`

const LiquidityIcon = () => (
	<svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M3.91002 17.3183L3.45602 18.2093L3.45603 18.2094L3.91002 17.3183ZM3.18166 16.59L2.29065 17.044L2.29065 17.044L3.18166 16.59ZM13.6517 16.59L14.5427 17.044V17.044L13.6517 16.59ZM12.9233 17.3183L13.3773 18.2093H13.3773L12.9233 17.3183ZM12.9233 6.84832L12.4693 7.73933L12.9233 6.84832ZM13.6517 7.57668L12.7607 8.03067L13.6517 7.57668ZM3.91002 6.84832L4.36401 7.73933H4.36401L3.91002 6.84832ZM3.18166 7.57668L2.29065 7.12269L3.18166 7.57668ZM17.8183 12.4233L18.7093 12.8773V12.8773L17.8183 12.4233ZM17.09 13.1517L17.544 14.0427H17.544L17.09 13.1517ZM17.09 2.68166L17.544 1.79065L17.544 1.79065L17.09 2.68166ZM17.8183 3.41002L18.7094 2.95603L18.7093 2.95602L17.8183 3.41002ZM8.07668 2.68166L7.62269 1.79065L8.07668 2.68166ZM7.34832 3.41002L8.23933 3.86401V3.86401L7.34832 3.41002ZM9.41667 10C9.41667 9.44772 8.96895 9 8.41667 9C7.86438 9 7.41667 9.44772 7.41667 10H9.41667ZM7.41667 14.1667C7.41667 14.719 7.86438 15.1667 8.41667 15.1667C8.96895 15.1667 9.41667 14.719 9.41667 14.1667H7.41667ZM10.5 13.0833C11.0523 13.0833 11.5 12.6356 11.5 12.0833C11.5 11.531 11.0523 11.0833 10.5 11.0833V13.0833ZM6.33333 11.0833C5.78105 11.0833 5.33333 11.531 5.33333 12.0833C5.33333 12.6356 5.78105 13.0833 6.33333 13.0833V11.0833ZM11.1667 16.5H5.66667V18.5H11.1667V16.5ZM4 14.8333V9.33333H2V14.8333H4ZM5.66667 16.5C5.18346 16.5 4.88119 16.4992 4.65344 16.4806C4.43736 16.463 4.3778 16.4344 4.36401 16.4273L3.45603 18.2094C3.79875 18.384 4.15081 18.4462 4.49057 18.474C4.81866 18.5008 5.21646 18.5 5.66667 18.5V16.5ZM2 14.8333C2 15.2835 1.99922 15.6813 2.02603 16.0094C2.05379 16.3492 2.11602 16.7012 2.29065 17.044L4.07266 16.136C4.06564 16.1222 4.03704 16.0626 4.01939 15.8466C4.00078 15.6188 4 15.3165 4 14.8333H2ZM4.36401 16.4273C4.23856 16.3634 4.13658 16.2614 4.07266 16.136L2.29065 17.044C2.54631 17.5457 2.95426 17.9537 3.45602 18.2093L4.36401 16.4273ZM12.8333 14.8333C12.8333 15.3165 12.8326 15.6188 12.8139 15.8466C12.7963 16.0626 12.7677 16.1222 12.7607 16.136L14.5427 17.044C14.7173 16.7012 14.7795 16.3492 14.8073 16.0094C14.8341 15.6813 14.8333 15.2835 14.8333 14.8333H12.8333ZM11.1667 18.5C11.6169 18.5 12.0147 18.5008 12.3428 18.474C12.6825 18.4462 13.0346 18.384 13.3773 18.2093L12.4693 16.4273C12.4555 16.4344 12.396 16.463 12.1799 16.4806C11.9521 16.4992 11.6499 16.5 11.1667 16.5V18.5ZM12.7607 16.136C12.6968 16.2614 12.5948 16.3634 12.4693 16.4273L13.3773 18.2093C13.8791 17.9537 14.287 17.5457 14.5427 17.044L12.7607 16.136ZM11.1667 7.66667C11.6499 7.66667 11.9521 7.66744 12.1799 7.68605C12.396 7.70371 12.4555 7.7323 12.4693 7.73933L13.3773 5.95732C13.0346 5.78269 12.6825 5.72045 12.3428 5.69269C12.0147 5.66589 11.6169 5.66667 11.1667 5.66667V7.66667ZM14.8333 9.33333C14.8333 8.88312 14.8341 8.48532 14.8073 8.15724C14.7795 7.81748 14.7173 7.46542 14.5427 7.12269L12.7607 8.03067C12.7677 8.04447 12.7963 8.10402 12.8139 8.3201C12.8326 8.54785 12.8333 8.85012 12.8333 9.33333H14.8333ZM12.4693 7.73933C12.5948 7.80324 12.6968 7.90523 12.7607 8.03067L14.5427 7.12269C14.287 6.62093 13.8791 6.21298 13.3773 5.95732L12.4693 7.73933ZM5.66667 5.66667C5.21646 5.66667 4.81866 5.66589 4.49057 5.69269C4.15081 5.72045 3.79875 5.78269 3.45603 5.95732L4.36401 7.73933C4.3778 7.7323 4.43736 7.70371 4.65344 7.68605C4.88119 7.66744 5.18346 7.66667 5.66667 7.66667V5.66667ZM4 9.33333C4 8.85012 4.00078 8.54785 4.01939 8.3201C4.03704 8.10402 4.06563 8.04447 4.07266 8.03067L2.29065 7.12269C2.11602 7.46542 2.05379 7.81748 2.02603 8.15724C1.99922 8.48532 2 8.88312 2 9.33333H4ZM3.45603 5.95732C2.95426 6.21298 2.54631 6.62093 2.29065 7.12269L4.07266 8.03067C4.13658 7.90523 4.23856 7.80324 4.36401 7.73933L3.45603 5.95732ZM9.83333 3.5H15.3333V1.5H9.83333V3.5ZM17 5.16667V10.6667H19V5.16667H17ZM17 10.6667C17 11.1499 16.9992 11.4521 16.9806 11.6799C16.963 11.896 16.9344 11.9555 16.9273 11.9693L18.7093 12.8773C18.884 12.5346 18.9462 12.1825 18.974 11.8428C19.0008 11.5147 19 11.1169 19 10.6667H17ZM15.3333 14.3333C15.7835 14.3333 16.1813 14.3341 16.5094 14.3073C16.8492 14.2795 17.2012 14.2173 17.544 14.0427L16.636 12.2607C16.6222 12.2677 16.5626 12.2963 16.3466 12.3139C16.1188 12.3326 15.8165 12.3333 15.3333 12.3333V14.3333ZM16.9273 11.9693C16.8634 12.0948 16.7614 12.1968 16.636 12.2607L17.544 14.0427C18.0457 13.787 18.4537 13.3791 18.7093 12.8773L16.9273 11.9693ZM15.3333 3.5C15.8165 3.5 16.1188 3.50078 16.3466 3.51939C16.5626 3.53704 16.6222 3.56564 16.636 3.57266L17.544 1.79065C17.2012 1.61602 16.8492 1.55379 16.5094 1.52603C16.1813 1.49922 15.7835 1.5 15.3333 1.5V3.5ZM19 5.16667C19 4.71646 19.0008 4.31866 18.974 3.99057C18.9462 3.65081 18.884 3.29875 18.7094 2.95603L16.9273 3.86401C16.9344 3.8778 16.963 3.93736 16.9806 4.15344C16.9992 4.38119 17 4.68346 17 5.16667H19ZM16.636 3.57266C16.7614 3.63658 16.8634 3.73856 16.9273 3.86401L18.7093 2.95602C18.4537 2.45426 18.0457 2.04631 17.544 1.79065L16.636 3.57266ZM9.83333 1.5C9.38312 1.5 8.98532 1.49922 8.65724 1.52603C8.31748 1.55379 7.96542 1.61602 7.62269 1.79065L8.53067 3.57266C8.54447 3.56563 8.60402 3.53704 8.8201 3.51939C9.04785 3.50078 9.35012 3.5 9.83333 3.5V1.5ZM8.16667 5.16667C8.16667 4.68346 8.16744 4.38119 8.18605 4.15344C8.20371 3.93736 8.2323 3.8778 8.23933 3.86401L6.45732 2.95603C6.28269 3.29875 6.22045 3.65081 6.19269 3.99057C6.16589 4.31866 6.16667 4.71646 6.16667 5.16667H8.16667ZM7.62269 1.79065C7.12093 2.04631 6.71298 2.45426 6.45732 2.95603L8.23933 3.86401C8.30324 3.73856 8.40523 3.63658 8.53067 3.57266L7.62269 1.79065ZM8.16667 6.66667V5.16667H6.16667V6.66667H8.16667ZM5.66667 7.66667H7.16667V5.66667H5.66667V7.66667ZM7.16667 7.66667H11.1667V5.66667H7.16667V7.66667ZM15.3333 12.3333H13.8333V14.3333H15.3333V12.3333ZM12.8333 9.33333V13.3333H14.8333V9.33333H12.8333ZM12.8333 13.3333V14.8333H14.8333V13.3333H12.8333ZM7.41667 10V12.0833H9.41667V10H7.41667ZM7.41667 12.0833V14.1667H9.41667V12.0833H7.41667ZM10.5 11.0833H8.41667V13.0833H10.5V11.0833ZM8.41667 11.0833H6.33333V13.0833H8.41667V11.0833Z" fill="white" />
	</svg>
)

const ImportIcon = () => (
	<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M9.99967 3.3335V10.8335M13.333 8.3335L10.5889 11.0776C10.2635 11.403 9.73586 11.403 9.41042 11.0776L6.66634 8.3335M16.6663 16.6668H3.33301" stroke="white" strokeWidth="2" strokeLinecap="round" />
	</svg>
)

const useCachedData = (key: string, data: any) => {
	useEffect(() => {
		if (data) {
			sessionStorage.setItem(key, JSON.stringify(data, (_, v) => typeof v === 'bigint' ? v.toString() : v))
		}
	}, [key, data])
}

const DropDown = ({ items, active, setActive, ...props }: { items: any, active: string, setActive: (index: string) => void, props?: any }) => {
	const [showDropDown, setDropDown] = useState(false)
	const [targetRef, setTargetRef] = useState<HTMLDivElement | null>(null)
	useOnClickOutside(
		targetRef,
		useCallback(() => {
			setDropDown(false);
		}, [setDropDown])
	)
	return <DropDownWrapper {...props} className={showDropDown ? "open" : ""} ref={setTargetRef} onClick={() => setDropDown(!showDropDown)}>
		<Text fontSize={14}>{items[active]}</Text>
		<IconButton scale="xs" variant="text">
			{
				showDropDown
					? <ChevronUpIcon />
					: <ChevronDownIcon />
			}
		</IconButton>
		{
			showDropDown &&
			<DropDownBox>
				{
					Object.entries(items).map(([index, item]) =>
						item
							? <DropDownItem key={index} onClick={() => {
								setActive(index)
								setDropDown(false)
							}}>
								<Text fontSize={14}>{String(item)}</Text>
								{
									index === active &&
									<CheckmarkIcon color="primary" />
								}
							</DropDownItem>
							: <hr />
					)
				}
			</DropDownBox>
		}
	</DropDownWrapper>
}

const CreatePoolButtonBox = ({ onClickItem, ...props }: { onClickItem: (index: string) => void, props?: any }) => {
	const { t } = useTranslation()
	const [showDropDown, setDropDown] = useState(false)
	const [targetRef, setTargetRef] = useState<HTMLDivElement | null>(null)
	useOnClickOutside(
		targetRef,
		useCallback(() => {
			setDropDown(false);
		}, [setDropDown])
	)
	return <ButtonWrapper ref={setTargetRef}>
		<CreatePoolButton onClick={() => setDropDown(!showDropDown)}>
			<FlexGap gap="8px" alignItems="center" justifyContent="center" flex={1} pl={["40px", "60px", "72px"]}>
				<LiquidityIcon />
				<Text fontSize={[18, 18, 20]} textAlign="center">Add Liquidity</Text>
			</FlexGap>
			<Flex width={["40px", "60px", "72px"]} borderLeft="1px solid #FFFFFF26" ml="auto" alignItems="center" justifyContent="center">
				<ChevronDownIcon width={24} />
			</Flex>
		</CreatePoolButton>
		{
			showDropDown &&
			<CreatePoolDropDown>
				<Box onClick={() => onClickItem("v3")}>
					<Text fontSize={16}>{t("V3 Liquidity")}</Text>
					<Text fontSize={13} color="textDisabled">{t("Provide Custom or Full Range V3 Liquidity")}</Text>
				</Box>
				<Box onClick={() => onClickItem("stable")}>
					<Text fontSize={16}>{t("pStable Pool")}</Text>
					<Text fontSize={13} color="textDisabled">{t("Get pUSD by providing liquidity for pDAI-pUSDC-pUSDT")}</Text>
				</Box>
			</CreatePoolDropDown>
		}
	</ButtonWrapper>
}

const FilterBox = ({ onSearch, filter, onFilter }: any) => {
	const [searchQuery, setSearchQuery] = useState("")
	const [isSearch, setSearch] = useState(false)
	if (isSearch)
		return <FlexGap gap="8px" mt="20px">
			<StyledIconButton variant="subtle" onClick={() => setSearch(false)}>
				<CloseIcon width={24} />
			</StyledIconButton>
			<SearchBox>
				<SearchIcon width={24} />
				<input
					placeholder="Search all"
					value={searchQuery}
					onChange={(e) => {
						const value = e.target.value?.trim()
						setSearchQuery(value)
						onSearch(value)
					}}
				/>
			</SearchBox>
		</FlexGap>
	return <FlexGap gap="8px" mt="20px">
		<StyledIconButton variant="subtle" onClick={() => setSearch(true)}>
			<SearchIcon width={24} />
		</StyledIconButton>
		<DropDown items={{ /* bump: "Bump Order", */ volume1d: "Volume", liquidity: "Liquidity", apr1d: "APR" }} setActive={(item) => {
			onFilter(item)
		}} active={filter} />
	</FlexGap>
}

const PoolsPage = ({
	version, query, sortField, sortDesc, last, page, setPages, tick = undefined
}) => {
	const { data: poolsData, status: poolsLoading, reload } = useAllPools(version, query, sortField, sortDesc, page)

	const pools = useMemo(() => poolsData ?? [], [poolsData])
	const isLoading = useMemo(() => {
		return poolsLoading === "loading"
	}, [poolsLoading])

	const { observerRef, isIntersecting } = useIntersectionObserver()

	useEffect(() => {
		if (isIntersecting) {
			setPages(p => p === page + 1 ? p + 1 : p)
		}
	}, [isIntersecting, page, setPages])

	useEffect(() => {
		if (tick)
			reload()
	}, [tick, reload])

	return isLoading
		? <CustomLoader />
		: <>
			{
				pools.map((pool) => (
					<PoolItem key={pool.address} pool={pool} onDone={reload} />
				))
			}
			{
				pools.length === 20 && last &&
				<div ref={observerRef} />
			}
		</>
}

const Liquidity = () => {
	const { t } = useTranslation()
	const { account } = useAccountActiveChain()
	// const router = useRouter()

	const [tick, setTick] = useState<any>()
	const [sortField, setSortField] = useState("liquidity")
	// const [sortDesc, setSortDesc] = useState(true)
	const [query, setQuery] = useState("")
	const [activeTab, setActiveTab] = useState(ActiveTab.ALL)
	const [pages, setPages] = useState(1)
	const [expanded, setExpanded] = useState<string>()

	// const pointsFarmingContract = usePointsFarmingContract()
	// const u = useUserV3FarmPositions(account)

	const { data: stablePools, reload: reloadStablePools } = useAllPools('stable', query)
	const { data: v3Positions } = useV3Positions(account)

	const [onPresentIncreaseV3Modal] = useModal(
		<IncreaseModal key={`increase-v3-modal`} onDone={() => setTick(Date.now())} />,
		true,
		true,
		`increase-v3-liquidity-modal`
	)

	const [onPresentIncreaseStableModal] = useModal(
		<AddModalStable key={`increase-stable-modal`} onDone={() => reloadStablePools()} />,
		true,
		true,
		`increase-stable-liquidity-modal`
	)

	const modalFinder = useModalV2()

	const onFindPoolModal = () => {
		modalFinder.onOpen()
	}

	const sortBy = useCallback((field) => {
		setSortField(field)
	}, [])

	const handleCreatePool = useCallback((item) => {
		if (item === "v3")
			onPresentIncreaseV3Modal()
		else if (item === "stable")
			onPresentIncreaseStableModal()
	}, [onPresentIncreaseStableModal, onPresentIncreaseV3Modal])

	const filteredV3Positions = useMemo(() => {
		return v3Positions?.filter(({ pool }) => {
			if (!query)
				return true
			const keywords = query.toLowerCase().split(/\W+/)
			if (keywords.map(keyword =>
				pool.address.toLowerCase().includes(keyword) ||
				pool.token0.name.toLowerCase().includes(keyword) ||
				pool.token0.symbol.toLowerCase().includes(keyword) ||
				pool.token1.name.toLowerCase().includes(keyword) ||
				pool.token1.symbol.toLowerCase().includes(keyword)
			).reduce((result, row) => result && row, true))
				return true
			return false
		}) ?? []
	}, [v3Positions, query])

	// console.log("filteredV3Positions", filteredV3Positions)

	const [searchQuery, setSearchQuery] = useState("")

	const debouncedSetQuery = useCallback((value: string) => {
		const timeoutId = setTimeout(() => {
			setQuery(value)
		}, 300)
		return () => clearTimeout(timeoutId)
	}, [])

	useEffect(() => {
		debouncedSetQuery(searchQuery)
	}, [searchQuery, debouncedSetQuery])

	useEffect(() => {
		setPages(1)
	}, [query, sortField])

	// useEffect(() => {
	// 	router.replace(activeTab === ActiveTab.ALL ? '/liquidity' : '/positions', undefined, { shallow: true })
	// }, [activeTab, router])

	return (
		<PageStyled>
			<TabMenu>
				<TabMenuItem selected={activeTab === ActiveTab.ALL} fontSize={[14, 18, 20]} onClick={() => setActiveTab(ActiveTab.ALL)}>
					Pools
					{
						activeTab === ActiveTab.ALL
							? <Corner className="top-right" size={24} color="#0000004D" style={{ clipPath: 'inset(6px 0 0 0)' }} />
							: <Corner className="bottom-right" size={24} color="#FFFFFF08" />
					}
				</TabMenuItem>
				<TabMenuItem selected={activeTab === ActiveTab.POSITIONS} fontSize={[14, 18, 20]} onClick={() => setActiveTab(ActiveTab.POSITIONS)}>
					Positions
					{
						activeTab === ActiveTab.POSITIONS
							? <Corner className="top-left" size={24} color="#0000004D" style={{ clipPath: 'inset(6px 0 0 0)' }} />
							: <Corner className="bottom-left" size={24} color="#FFFFFF08" />
					}
					{/* {
						activeTab !== ActiveTab.POSITIONS
							? <>
								<Corner className="bottom-left" size={24} color={activeTab === ActiveTab.ALL ? "#FFFFFF08" : "#0000004D"} />
								<Corner className="bottom-right" size={24} color={activeTab === ActiveTab.EARN ? "#FFFFFF08" : "#0000004D"} />
							</>
							: <>
								<Corner className="top-left" size={24} color="#0000004D" style={{ clipPath: 'inset(6px 0 0 0)' }} />
								<Corner className="top-right" size={24} color="#0000004D" style={{ clipPath: 'inset(6px 0 0 0)' }} />
							</>
					} */}
				</TabMenuItem>
				{/* <TabMenuItem selected={activeTab === ActiveTab.EARN} fontSize={[14, 18, 20]} onClick={() => setActiveTab(ActiveTab.EARN)}>
					Earn
					{
						activeTab === ActiveTab.EARN
							? <Corner className="top-left" size={24} color="#0000004D" style={{ clipPath: 'inset(6px 0 0 0)' }} />
							: <Corner className="bottom-left" size={24} color={activeTab === ActiveTab.POSITIONS ? "#FFFFFF08" : "#0000004D"} />
					}
				</TabMenuItem> */}
			</TabMenu>
			<Box p="16px" bg="#FFFFFF08" borderRadius="24px" position="relative">
				<Corner className="top-left" size={24} color={activeTab === ActiveTab.ALL ? "#FFFFFF08" : "#0000004D"} />
				<Corner className="top-right" size={24} color={activeTab === ActiveTab.POSITIONS ? "#FFFFFF08" : "#0000004D"} />
				{
					account
						? <CreatePoolButtonBox onClickItem={handleCreatePool} />
						: <ConnectWalletButton width="100%" style={{ fontSize: '20px', height: '72px' }} />
				}
				{activeTab === ActiveTab.ALL && (
					<>
						<FilterBox onSearch={debouncedSetQuery} onFilter={sortBy} filter={sortField} />
						<FlexGap gap="12px" flexDirection="column" mt="20px">
							{
								stablePools?.length > 0 &&
								<PoolItem pool={stablePools[0]} onDone={() => reloadStablePools()} />
							}
							{
								new Array(pages).fill(0).map((_, page) =>
									<PoolsPage key={`pools-${page}`} version="v3" query={query} sortField={sortField} sortDesc={true} page={page} setPages={setPages} last={page + 1 === pages} />
								)
							}
						</FlexGap>
					</>
				)}
				{activeTab === ActiveTab.POSITIONS && !!account && (
					<>
						{v3Positions?.length > 0 && (
							<SearchBox mt="20px">
								<SearchIcon width={24} />
								<input
									placeholder="Search by name, symbol or address"
									value={searchQuery}
									onChange={(e) => {
										setSearchQuery(e.target.value)
									}}
								/>
								{
									searchQuery?.length > 0 &&
									<CloseIcon width={24} onClick={() => {
										setSearchQuery('')
									}} />
								}
							</SearchBox>
						)}
						{
							filteredV3Positions.map(({ pool, positions }) => (
								<V3Position
									key={`position-${pool.address}`}
									pool={pool}
									positions={positions}
									expanded={expanded === `position-${pool.address}`}
									setExpanded={(exp: boolean) => setExpanded(exp ? `position-${pool.address}` : undefined)}
								/>
							))
						}
						{
							stablePools?.map(pool => (
								<StablePosition
									key={`position-${pool.address}`}
									pool={pool}
									expanded={expanded === `position-${pool.address}`}
									setExpanded={(exp: boolean) => setExpanded(exp ? `position-${pool.address}` : undefined)}
								/>
							))
						}
					</>
				)}
			</Box>
		</PageStyled>
	)
}

export default Liquidity